import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Container,
  Grid,
  Button,
  TextField,
  Box,
} from "@mui/material";
import { Players } from "src/_mock/players";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { createBlobInContainer } from "src/utils/azureBlob";
import PlayerFormFields from "src/components/players/PlayerFormFields";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const PlayerDetailPage = () => {
  const { playerId } = useParams();
  const navigate = useNavigate();

  const [editedPlayer, setEditedPlayer] = useState(() => {
    if (!playerId || playerId === "new") {
      return {
        id: Date.now(),
        name: "",
        sport: "",
        league: "",
        team: "",
        position: "",
        headshot: "",
        avatar: "",
        altImage: "",
        feedId: "",
        nickname: "",
        number: "",
        nationality: "",
        nationalityFlag: "",
        injuryStatus: "",
      };
    }

    const foundPlayer = Players.find((p) => p.id.toString() === playerId);
    return (
      foundPlayer || {
        id: Date.now(),
        name: "",
        sport: "",
        league: "",
        team: "",
        position: "",
        headshot: "",
        avatar: "",
        altImage: "",
        feedId: "",
        nickname: "",
        number: "",
        nationality: "",
        nationalityFlag: "",
        injuryStatus: "",
      }
    );
  });

  if (!editedPlayer && playerId !== "new") {
    return (
      <Container>
        <Typography variant="h6">Player not found</Typography>
      </Container>
    );
  }
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedPlayer((prevPlayer) => ({
      ...prevPlayer,
      [name]: value,
    }));
  };

  const uploadFiles = (event) => {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      const imageType = event.target.dataset.imageType;
      if (!file || !imageType) return;

      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
      const maxSize = 2 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        alert("Only JPG, JPEG, PNG, WEBP, and GIF formats are allowed.");
        event.target.value = null;
        return;
      }

      if (file.size > maxSize) {
        alert("File size should not exceed 2MB.");
        event.target.value = null;
        return;
      }

      const newFileName = file.name.replace(/\s+/g, "_");
      const extension = newFileName.substring(newFileName.lastIndexOf("."));
      const safeFileName = `image_${Date.now()}${extension}`;

      const renamedFile = new File([file], safeFileName, { type: file.type });

      const uploadFilesAsync = async () => {
        try {
          await createBlobInContainer(renamedFile);
          const containerName = process.env.REACT_APP_STORAGE_CONTAINER;
          const storageAccountName = process.env.REACT_APP_STORAGERESOURCENAME;
          const imageUrl = `https://${storageAccountName}.blob.core.windows.net/${containerName}/${renamedFile.name}`;

          setEditedPlayer((prevPlayer) => ({
            ...prevPlayer,
            [imageType]: imageUrl,
          }));
        } catch (err) {
          alert("Error uploading file");
        } finally {
          event.target.value = null;
        }
      };

      uploadFilesAsync();
    }
  };

  const handleSave = () => {
    navigate("/dashboard/players");
  };

  const handleCancel = () => {
    navigate("/dashboard/players");
  };

  return (
    <Container maxWidth="lg" sx={{ display: "flex", gap: 4, py: 4 }}>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ mb: 5, display: "flex", alignItems: "center" }}
        >
          Players
          {playerId !== "new" && editedPlayer.name && (
            <>
              <ArrowForwardIosIcon sx={{ mx: 1 }} />
              {editedPlayer.name}
            </>
          )}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button variant="contained" color="primary" onClick={handleSave}>
                Save
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 3,
            paddingTop: "40px",
          }}
        >
          <PlayerFormFields
            editedPlayer={editedPlayer}
            handleInputChange={handleInputChange}
          />
          <Box>
            <Box sx={{ display: "flex", gap: 3 }}>
              {[
                {
                  label: "Headshot",
                  imageType: "headshot",
                  src: editedPlayer.headshot,
                },
                {
                  label: "Avatar",
                  imageType: "avatar",
                  src: editedPlayer.avatar,
                },
                {
                  label: "Alt Image",
                  imageType: "altImage",
                  src: editedPlayer.altImage,
                },
              ].map(({ label, imageType, src }) => (
                <Box key={imageType}>
                  <Typography
                    sx={{ mb: 2, fontWeight: "500", fontSize: 16 }}
                    align="center"
                  >
                    {label}
                  </Typography>
                  <Box
                    sx={{
                      width: 211.82,
                      height: 210.34,
                      bgcolor: "#FFFFFF",
                      borderRadius: "21px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    {src ? (
                      <img
                        alt={label}
                        src={src}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                        }}
                      />
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        No image
                      </Typography>
                    )}
                  </Box>
                  <Button component="label" fullWidth>
                    Upload
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      hidden
                      data-image-type={imageType}
                      onChange={uploadFiles}
                    />
                  </Button>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: "flex", gap: 10, paddingTop: 5 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  width: 300,
                }}
              >
                <TextField
                  select
                  label="+ Add League"
                  fullWidth
                  variant="outlined"
                  size="small"
                  SelectProps={{
                    native: false,
                    IconComponent: KeyboardArrowDownIcon,
                    sx: { borderRadius: "50px" },
                  }}
                  InputProps={{
                    sx: { borderRadius: "50px" },
                  }}
                />

                <TextField
                  select
                  label="+ Add Team"
                  fullWidth
                  variant="outlined"
                  size="small"
                  SelectProps={{
                    native: false,
                    IconComponent: KeyboardArrowDownIcon,
                  }}
                  InputProps={{
                    sx: { borderRadius: "50px" },
                  }}
                />
                <Box marginTop={3}>
                  <TextField
                    label="+ Add Feed ID"
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{
                      sx: { borderRadius: "50px" },
                    }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  width: 250,
                }}
              >
                <TextField
                  select
                  label="+ Add League"
                  fullWidth
                  variant="outlined"
                  size="small"
                  SelectProps={{
                    native: false,
                    IconComponent: KeyboardArrowDownIcon,
                    sx: { borderRadius: "50px" },
                  }}
                  InputProps={{
                    sx: { borderRadius: "50px" },
                  }}
                />

                <TextField
                  select
                  label="+ Add Team"
                  fullWidth
                  variant="outlined"
                  size="small"
                  SelectProps={{
                    native: false,
                    IconComponent: KeyboardArrowDownIcon,
                  }}
                  InputProps={{
                    sx: { borderRadius: "50px" },
                  }}
                />
                <Box marginTop={3}>
                  <TextField
                    label="+ Add Feed ID"
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{
                      sx: { borderRadius: "50px" },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default PlayerDetailPage;
